import { computed } from 'vue';
import type { SwadeCharacterInterface } from '@/types/characters';
import type { ContainerEffect } from '@/types/items';
import { enterableEffectFor } from '@/appdata/container-entries';
import { CONTAINER_PATHS } from '@/appdata/container-targets';

/**
 * Which rows of an edge or hindrance list ask the player for a trapping.
 *
 * A container.* trapping modification authored with a null value asks the player for the trapping
 * (spec 2026-09-15): the row shows a text field (TrappingEntryField) bound to the ledger entry the
 * fold would read last. A host needs the answer itself, not only the field, because its title
 * stays the raw catalog name while the field shows (spec 2026-10-05).
 *
 * One lookup per row per recompute, reactive through the character's ledger. The computed
 * deliberately does NOT read `entered`: the field's own enteredValueFor call tracks it, and
 * enterContainerEffectValue always assigns a fresh map or deletes the member, so a write
 * re-renders the field without rebuilding this map. Do not move that read in here.
 *
 * `character` is a getter, as useGuidedElementPicker's is: the sheet's lists hold a
 * CharacterSheetInterface and the guided steps a SwadeCharacterInterface, and a getter takes
 * either as it is.
 */
export function useTrappingEntries(
    character: () => SwadeCharacterInterface | null | undefined,
    path: 'container.edges' | 'container.hindrances',
) {
    const entries = computed(() => {
        const found = new Map<string, ContainerEffect>();
        const sheet = character();
        if (!sheet) return found;
        const rows: { uuid?: string | null }[] = sheet[CONTAINER_PATHS[path]] ?? [];
        for (const row of rows) {
            const effect = enterableEffectFor(sheet, path, row, 'trapping');
            if (effect && typeof row.uuid === 'string') found.set(row.uuid, effect);
        }
        return found;
    });

    /** The entry this row's field binds to, or null when the row asks for nothing. */
    const entryFor = (row: { uuid?: string | null } | null | undefined): ContainerEffect | null =>
        (typeof row?.uuid === 'string' ? entries.value.get(row.uuid) : undefined) ?? null;

    return { entryFor };
}
